import { useState, useRef, useCallback, useEffect } from 'react';
import { logError } from '@/lib/sentry';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Upload,
  CheckCircle,
  Minus,
  Settings,
  FileSpreadsheet,
  Trash2,
  BarChart3,
  Lightbulb,
  Loader2,
  ArrowLeft,
  AlertTriangle,
} from 'lucide-react';
import {
  parseTemplateFile,
  validateTemplateFile,
  MAPPING_FIELDS,
  getDefaultTemplateName,
  type ParsedTemplate,
  type ParsedSheet,
  type ColumnMapping,
  type MappingFieldValue,
  type DeliveryTemplate,
} from '@/utils/templateParser';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/supabase/client';

// ==================== 타입 ====================

type DialogStep = 'upload' | 'sheet-select' | 'mapping' | 'manage';

interface DeliveryTemplateManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  savedTemplate: DeliveryTemplate | null;
  onSaveTemplate: (template: DeliveryTemplate) => Promise<void>;
  onDeleteTemplate: () => Promise<void>;
}

// ==================== 간단한 자동 매핑 (Phase 2에서 AI로 대체) ====================

const KEYWORD_MAP: Record<string, MappingFieldValue> = {
  '성명': 'name',
  '이름': 'name',
  '받는분': 'name',
  '받는사람': 'name',
  '수취인': 'name',
  '수하인명': 'name',
  '전화': 'phone_number',
  '핸드폰': 'phone_number',
  '휴대폰': 'phone_number',
  '연락처': 'phone_number',
  '주소': 'address',
  '배송메': 'delivery_notes',
  '배송 메': 'delivery_notes',
  '메세지': 'delivery_notes',
  '메시지': 'delivery_notes',
  '품목': 'product_name',
  '상품명': 'product_name',
  '내품명': 'product_name',
  '닉네임': 'nickname',
  '별명': 'nickname',
};

function autoMapColumns(headers: string[]): ColumnMapping[] {
  const usedFields = new Set<MappingFieldValue>();

  return headers.map((header) => {
    const normalizedHeader = header.toLowerCase().replace(/\s/g, '');

    // "사용안함", "불필요항목" 등은 바로 매핑 안 함
    if (/사용안함|불필요|미사용/.test(normalizedHeader)) {
      return { columnName: header, mappedField: 'null' as MappingFieldValue };
    }

    for (const [keyword, field] of Object.entries(KEYWORD_MAP)) {
      if (normalizedHeader.includes(keyword.toLowerCase().replace(/\s/g, '')) && !usedFields.has(field)) {
        // 주소 분리 처리: "주소1" → address_base, "주소2" → address_detail
        if (field === 'address') {
          if (/주소\s*1|기본\s*주소/.test(header)) {
            if (!usedFields.has('address_base')) {
              usedFields.add('address_base');
              return { columnName: header, mappedField: 'address_base' as MappingFieldValue };
            }
          } else if (/주소\s*2|상세\s*주소/.test(header)) {
            if (!usedFields.has('address_detail')) {
              usedFields.add('address_detail');
              return { columnName: header, mappedField: 'address_detail' as MappingFieldValue };
            }
          }
        }

        // 전화번호: "기타연락처" 같은 건 스킵
        if (field === 'phone_number' && /기타/.test(header)) {
          return { columnName: header, mappedField: 'null' as MappingFieldValue };
        }

        usedFields.add(field);
        return { columnName: header, mappedField: field };
      }
    }

    return { columnName: header, mappedField: 'null' as MappingFieldValue };
  });
}

// ==================== 메인 컴포넌트 ====================

export default function DeliveryTemplateManager({
  open,
  onOpenChange,
  savedTemplate,
  onSaveTemplate,
  onDeleteTemplate,
}: DeliveryTemplateManagerProps) {
  // Step 관리
  const initialStep: DialogStep = savedTemplate ? 'manage' : 'upload';
  const [step, setStep] = useState<DialogStep>(initialStep);

  // 파일 업로드 상태
  const [isDragging, setIsDragging] = useState(false);
  const [parsedTemplate, setParsedTemplate] = useState<ParsedTemplate | null>(null);
  const [selectedSheetIndex, setSelectedSheetIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 매핑 상태
  const [templateName, setTemplateName] = useState('');
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzedCount, setAnalyzedCount] = useState(0);
  const [analyzingTotal, setAnalyzingTotal] = useState(0);
  const animationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mappingRequestIdRef = useRef(0);

  // 저장/삭제 중 상태
  const [isSaving, setIsSaving] = useState(false);

  // 삭제 확인
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Dialog open 시 초기 step 설정 + 닫힐 때 타이머 정리
  useEffect(() => {
    if (open) {
      setStep(savedTemplate ? 'manage' : 'upload');
      setParsedTemplate(null);
      setIsDragging(false);
    } else {
      // 다이얼로그 닫힐 때 애니메이션 타이머 정리 + in-flight 요청 무효화
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
      mappingRequestIdRef.current++;
      setIsAnalyzing(false);
      setShowDeleteConfirm(false);
    }
  }, [open, savedTemplate]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
    };
  }, []);

  // ==================== 매핑 시작 ====================

  const runMappingAnimation = useCallback((mappings: ColumnMapping[]) => {
    let count = 0;
    animationIntervalRef.current = setInterval(() => {
      count++;
      setAnalyzedCount(count);
      if (count >= mappings.length) {
        if (animationIntervalRef.current) {
          clearInterval(animationIntervalRef.current);
          animationIntervalRef.current = null;
        }
        setIsAnalyzing(false);
      }
    }, 80);
  }, []);

  const startMapping = useCallback(async (sheet: ParsedSheet, fileName: string) => {
    // 이전 애니메이션 타이머 정리
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }

    // 경합 방어: 새 요청 ID 발급
    const requestId = ++mappingRequestIdRef.current;

    setTemplateName(getDefaultTemplateName(fileName));
    setAnalyzedCount(0);
    setAnalyzingTotal(sheet.headers.length);
    setIsAnalyzing(true);
    setStep('mapping');

    const validFieldValues: Set<string> = new Set(MAPPING_FIELDS.map((f) => f.value));

    // AI 매핑 시도
    let mappings: ColumnMapping[] | null = null;
    try {
      const { data, error } = await supabase.functions.invoke('map-columns', {
        body: { headers: sheet.headers, sampleData: sheet.sampleData },
      });

      if (error) {
        logError('[DeliveryTemplateManager] AI 매핑 Edge Function 호출 실패', error);
      } else if (!data?.mappings || !Array.isArray(data.mappings)) {
        logError('[DeliveryTemplateManager] AI 매핑 응답 형식이 올바르지 않음', new Error(
          `응답 data.mappings가 배열이 아님: ${JSON.stringify(data).slice(0, 200)}`
        ));
      } else if (data.mappings.length !== sheet.headers.length) {
        logError('[DeliveryTemplateManager] AI 매핑 배열 길이 불일치', new Error(
          `expected=${sheet.headers.length}, received=${data.mappings.length}`
        ));
      } else {
        const validated = data.mappings.every(
          (m: { columnName?: string; mappedField?: string }) =>
            typeof m.columnName === 'string' && typeof m.mappedField === 'string' && validFieldValues.has(m.mappedField),
        );
        if (validated) {
          mappings = data.mappings.map((m: { columnName: string; mappedField: string }) => ({
            columnName: m.columnName,
            mappedField: m.mappedField as MappingFieldValue,
          }));
        } else {
          const invalidFields = data.mappings
            .filter((m: { mappedField?: string }) => !validFieldValues.has(m.mappedField ?? ''))
            .map((m: { columnName?: string; mappedField?: string }) => `${m.columnName}→${m.mappedField}`);
          logError('[DeliveryTemplateManager] AI 매핑 필드 검증 실패', new Error(
            `유효하지 않은 매핑: ${invalidFields.join(', ')}`
          ));
        }
      }
    } catch (err) {
      logError('[DeliveryTemplateManager] AI 매핑 요청 중 예외 발생 (네트워크 오류 등)', err);
    }

    // 경합 방어: 이 사이에 새 요청이 시작되었으면 무시
    if (requestId !== mappingRequestIdRef.current) return;

    // AI 실패 시 키워드 기반 폴백
    if (!mappings) {
      mappings = autoMapColumns(sheet.headers);
      toast.warning('키워드 기반으로 매핑했어요. 결과를 확인해주세요.');
    }

    setColumnMappings(mappings);
    runMappingAnimation(mappings);
  }, [runMappingAnimation]);

  // ==================== 파일 처리 ====================

  const handleFile = useCallback(async (file: File) => {
    const error = validateTemplateFile(file);
    if (error) {
      toast.error(error);
      return;
    }

    try {
      const parsed = await parseTemplateFile(file);

      if (parsed.sheets.length === 0 || parsed.sheets.every((s) => s.headers.length === 0)) {
        logError('[DeliveryTemplateManager] 업로드된 엑셀에서 헤더를 찾을 수 없음', new Error(
          `fileName=${file.name}, sheets=${parsed.sheets.length}, headersPerSheet=${parsed.sheets.map(s => s.headers.length).join(',')}`
        ));
        toast.error('엑셀 파일에서 헤더를 찾을 수 없습니다.');
        return;
      }

      setParsedTemplate(parsed);
      setSelectedSheetIndex(parsed.recommendedSheetIndex);

      if (parsed.sheets.length === 1) {
        // 시트가 1개면 바로 매핑 단계로
        startMapping(parsed.sheets[0], parsed.fileName);
      } else {
        setStep('sheet-select');
      }
    } catch (err) {
      logError('[DeliveryTemplateManager] 엑셀 파일 파싱 실패', err, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });
      toast.error('파일을 읽는 중 오류가 발생했습니다.');
    }
  }, [startMapping]);

  // ==================== 드래그 & 드롭 ====================

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // reset input
    if (e.target) e.target.value = '';
  }, [handleFile]);

  // ==================== 매핑 변경 ====================

  const handleMappingChange = useCallback((index: number, newField: MappingFieldValue) => {
    setColumnMappings((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], mappedField: newField };
      return updated;
    });
  }, []);

  // ==================== 저장 ====================

  const handleSave = useCallback(async () => {
    if (isSaving) return;
    if (!templateName.trim()) {
      toast.error('양식 이름을 입력해주세요.');
      return;
    }
    if (columnMappings.length === 0) {
      toast.error('매핑할 컬럼이 없습니다. 양식 파일을 다시 확인해주세요.');
      return;
    }
    if (columnMappings.every((m) => m.mappedField === 'null')) {
      toast.error('최소 1개 이상의 컬럼을 매핑해주세요.');
      return;
    }

    const template: DeliveryTemplate = {
      id: savedTemplate?.id,
      name: templateName.trim(),
      columnMappings,
      headerRowIndex: parsedTemplate?.sheets[selectedSheetIndex]?.headerRowIndex ?? 1,
    };

    setIsSaving(true);
    try {
      await onSaveTemplate(template);
      toast.success('택배사 양식이 저장되었습니다!');
      onOpenChange(false);
    } catch (err) {
      logError('[DeliveryTemplateManager] 양식 저장 실패', err, {
        templateName: template.name,
        templateId: template.id,
        columnCount: template.columnMappings.length,
      });
      toast.error('양식 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, templateName, columnMappings, parsedTemplate, selectedSheetIndex, onSaveTemplate, onOpenChange, savedTemplate]);

  // ==================== 삭제 ====================

  const handleDelete = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await onDeleteTemplate();
      setShowDeleteConfirm(false);
      toast.success('양식이 삭제되었습니다.');
      onOpenChange(false);
    } catch (err) {
      logError('[DeliveryTemplateManager] 양식 삭제 실패', err);
      toast.error('양식 삭제에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, onDeleteTemplate, onOpenChange]);

  // ==================== 시트 선택 후 다음 ====================

  const handleSheetNext = useCallback(() => {
    if (!parsedTemplate) return;
    const sheet = parsedTemplate.sheets[selectedSheetIndex];
    if (sheet.headers.length === 0) {
      toast.error('빈 시트입니다. 다른 시트를 선택해주세요.');
      return;
    }
    startMapping(sheet, parsedTemplate.fileName);
  }, [parsedTemplate, selectedSheetIndex, startMapping]);

  // ==================== 매핑 수정 (관리 → 매핑으로) ====================

  const handleEditMapping = useCallback(() => {
    if (!savedTemplate) return;
    setColumnMappings(savedTemplate.columnMappings);
    setTemplateName(savedTemplate.name);
    setIsAnalyzing(false);
    setStep('mapping');
  }, [savedTemplate]);

  // ==================== 렌더링 ====================

  const mappedCount = columnMappings.filter((m) => m.mappedField !== 'null').length;
  const totalCount = columnMappings.length;

  const dialogTitle = step === 'manage' ? '택배사 양식 관리' : '택배사 양식 설정';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-2xl max-h-[85vh] overflow-y-auto"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            document.body.style.pointerEvents = '';
          }}
          onInteractOutside={(e) => {
            if (showDeleteConfirm) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (showDeleteConfirm) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription className="sr-only">
              택배사 엑셀 양식을 업로드하고 컬럼 매핑을 설정합니다.
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: 파일 업로드 */}
          {step === 'upload' && (
            <StepUpload
              isDragging={isDragging}
              fileInputRef={fileInputRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onFileInputChange={handleFileInputChange}
              onBack={savedTemplate ? () => setStep('manage') : undefined}
            />
          )}

          {/* Step 1.5: 시트 선택 */}
          {step === 'sheet-select' && parsedTemplate && (
            <StepSheetSelect
              parsedTemplate={parsedTemplate}
              selectedSheetIndex={selectedSheetIndex}
              onSelectSheet={setSelectedSheetIndex}
              onBack={() => {
                setParsedTemplate(null);
                setStep('upload');
              }}
              onNext={handleSheetNext}
            />
          )}

          {/* Step 2: AI 분석 + 매핑 확인 */}
          {step === 'mapping' && (
            <StepMapping
              isAnalyzing={isAnalyzing}
              analyzedCount={analyzedCount}
              analyzingTotal={analyzingTotal}
              columnMappings={columnMappings}
              templateName={templateName}
              mappedCount={mappedCount}
              totalCount={totalCount}
              onTemplateNameChange={setTemplateName}
              onMappingChange={handleMappingChange}
              onBack={() => {
                if (!parsedTemplate && savedTemplate) {
                  // 매핑 수정에서 진입한 경우 → 관리 화면으로 복귀
                  setStep('manage');
                } else if (parsedTemplate && parsedTemplate.sheets.length > 1) {
                  setStep('sheet-select');
                } else {
                  setParsedTemplate(null);
                  setStep('upload');
                }
              }}
              onSave={handleSave}
              isSaving={isSaving}
            />
          )}

          {/* Step 3: 관리 화면 */}
          {step === 'manage' && savedTemplate && (
            <StepManage
              template={savedTemplate}
              onEditMapping={handleEditMapping}
              onDelete={() => setShowDeleteConfirm(true)}
              onNewTemplate={() => {
                setParsedTemplate(null);
                setStep('upload');
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* 삭제 확인 AlertDialog - 필요할 때만 마운트하여 Radix pointer-events 충돌 방지 */}
      {showDeleteConfirm && (
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent className="bg-white rounded-xl shadow-lg max-w-md mx-auto">
            <AlertDialogHeader className="space-y-3">
              <AlertDialogTitle className="text-lg font-semibold text-gray-900">
                양식을 삭제하시겠습니까?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm text-gray-600">
                이 양식을 삭제하면 이후 다운로드는 기본 양식으로 진행됩니다.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col sm:flex-row gap-2 pt-2">
              <AlertDialogCancel className="border-gray-300 hover:bg-gray-100">취소</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-500 hover:bg-red-600 text-white"
                onClick={handleDelete}
              >
                삭제하기
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

// ==================== Step 1: 파일 업로드 ====================

function StepUpload({
  isDragging,
  fileInputRef,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileInputChange,
  onBack,
}: {
  isDragging: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBack?: () => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        현재 사용 중인 택배사 엑셀 양식을 올려주세요.
        <br />
        <span className="text-gray-500">자동으로 컬럼을 분석해 매핑해드려요.</span>
      </p>

      {/* 드래그 & 드롭 영역 */}
      <div
        className={`border-2 border-dashed rounded-xl py-12 px-6 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-teal-400 bg-teal-50'
            : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        }`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-600">
          엑셀 파일을 드래그하거나
          <br />
          클릭하여 업로드하세요
        </p>
        <p className="text-xs text-gray-400 mt-2">.xlsx, .xls 파일 지원</p>
        <input
          ref={fileInputRef as React.RefObject<HTMLInputElement>}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={onFileInputChange}
        />
      </div>

      {/* 힌트 */}
      <div className="flex items-start gap-2 text-xs text-gray-500">
        <Lightbulb className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
        <span>빈 양식이나 데이터가 있는 파일 모두 가능해요.</span>
      </div>

      {/* 이전 버튼 (관리 화면에서 진입한 경우) */}
      {onBack && (
        <div className="pt-2">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            이전
          </Button>
        </div>
      )}
    </div>
  );
}

// ==================== Step 1.5: 시트 선택 ====================

function StepSheetSelect({
  parsedTemplate,
  selectedSheetIndex,
  onSelectSheet,
  onBack,
  onNext,
}: {
  parsedTemplate: ParsedTemplate;
  selectedSheetIndex: number;
  onSelectSheet: (index: number) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <div className="space-y-4">
      {/* 파일명 표시 */}
      <div className="flex items-center gap-2">
        <CheckCircle className="h-4 w-4 text-teal-500" />
        <span className="text-sm font-medium text-teal-700">{parsedTemplate.fileName}</span>
      </div>

      <p className="text-sm text-gray-600">시트를 선택해주세요:</p>

      {/* 시트 목록 */}
      <div className="space-y-2">
        {parsedTemplate.sheets.map((sheet, index) => {
          const isEmpty = sheet.headers.length === 0;
          return (
            <button
              key={index}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                isEmpty
                  ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                  : selectedSheetIndex === index
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 hover:border-teal-300'
              }`}
              onClick={() => !isEmpty && onSelectSheet(index)}
              disabled={isEmpty}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      isEmpty
                        ? 'border-gray-300'
                        : selectedSheetIndex === index
                          ? 'border-teal-500'
                          : 'border-gray-300'
                    }`}
                  >
                    {!isEmpty && selectedSheetIndex === index && (
                      <div className="w-2 h-2 rounded-full bg-teal-500" />
                    )}
                  </div>
                  <span className={`text-sm ${isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>{sheet.name}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {isEmpty && (
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      빈 시트
                    </span>
                  )}
                  {!isEmpty && /제목.*없|헤더.*없/.test(sheet.name) && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      헤더 없음
                    </span>
                  )}
                  {!isEmpty && index === parsedTemplate.recommendedSheetIndex && (
                    <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                      추천
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* 버튼 */}
      <div className="flex justify-between pt-2">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          이전
        </Button>
        <Button
          size="sm"
          className="bg-teal-500 hover:bg-teal-600 text-white"
          onClick={onNext}
        >
          다음
        </Button>
      </div>
    </div>
  );
}

// ==================== Step 2: AI 분석 + 매핑 ====================

function StepMapping({
  isAnalyzing,
  analyzedCount,
  analyzingTotal,
  columnMappings,
  templateName,
  mappedCount,
  totalCount,
  onTemplateNameChange,
  onMappingChange,
  onBack,
  onSave,
  isSaving,
}: {
  isAnalyzing: boolean;
  analyzedCount: number;
  analyzingTotal: number;
  columnMappings: ColumnMapping[];
  templateName: string;
  mappedCount: number;
  totalCount: number;
  onTemplateNameChange: (name: string) => void;
  onMappingChange: (index: number, field: MappingFieldValue) => void;
  onBack: () => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  if (isAnalyzing) {
    const progressPercent = analyzingTotal > 0 ? Math.round((analyzedCount / analyzingTotal) * 100) : 0;

    return (
      <div className="space-y-4 py-6">
        <div className="flex items-center justify-center gap-2 text-teal-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm font-medium">컬럼을 분석하고 있어요...</span>
        </div>

        {/* Progress bar */}
        <div className="max-w-sm mx-auto space-y-1">
          <Progress value={progressPercent} className="h-2" />
          <p className="text-xs text-gray-500 text-right">{analyzedCount} / {analyzingTotal}</p>
        </div>

        <div className="space-y-2 max-w-sm mx-auto">
          {columnMappings.slice(0, analyzedCount).map((mapping, i) => {
            const isMapped = mapping.mappedField !== 'null';
            const fieldLabel = MAPPING_FIELDS.find((f) => f.value === mapping.mappedField)?.label || '매핑 안 함';
            return (
              <div
                key={i}
                className="flex items-center gap-2 text-sm text-gray-600 animate-in fade-in slide-in-from-bottom-1 duration-200"
              >
                <span className={isMapped ? 'text-teal-700' : 'text-gray-500'}>{fieldLabel}</span>
                <span className={isMapped ? 'text-teal-400' : 'text-gray-300'}>{isMapped ? '→' : '·'}</span>
                <span className="text-gray-800 font-mono">{mapping.columnName}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 요약 배지 */}
      <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-lg p-3">
        <BarChart3 className="h-4 w-4 text-teal-600" />
        <span className="text-sm font-medium text-teal-800">
          {totalCount}개 컬럼 중 {mappedCount}개가 매핑되었어요
        </span>
      </div>

      {/* 양식 이름 */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-gray-700">양식 이름</label>
        <Input
          value={templateName}
          onChange={(e) => onTemplateNameChange(e.target.value)}
          placeholder="예: CJ택배"
          className="border-gray-300 focus:ring-teal-500 focus:border-teal-500"
        />
      </div>

      {/* 매핑 테이블 */}
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        {/* 테이블 헤더 — 모바일에서 숨김 */}
        <div className="hidden sm:grid grid-cols-[180px_24px_1fr_28px] gap-2 bg-gray-50 px-4 py-2.5 border-b border-gray-200">
          <span className="text-xs font-medium text-gray-500 uppercase">셀러헬퍼 데이터</span>
          <span />
          <span className="text-xs font-medium text-gray-500 uppercase">택배사 엑셀 컬럼</span>
          <span />
        </div>

        {/* 테이블 행 */}
        <div className="max-h-[320px] overflow-y-auto">
          {columnMappings.map((mapping, index) => {
            const isMapped = mapping.mappedField !== 'null';
            return (
              <div
                key={index}
                className={`flex flex-col sm:grid sm:grid-cols-[180px_24px_1fr_28px] gap-1 sm:gap-2 sm:items-center px-4 py-2.5 border-t border-gray-100 ${
                  isMapped ? 'bg-white' : 'border-l-2 border-l-gray-200 bg-gray-50/30'
                }`}
              >
                {/* 모바일: 1줄 — 드롭다운 + 상태아이콘 */}
                <div className="flex items-center gap-2 sm:contents">
                  {/* 셀러헬퍼 필드 선택 (드롭다운) */}
                  <select
                    value={mapping.mappedField}
                    onChange={(e) => onMappingChange(index, e.target.value as MappingFieldValue)}
                    className={`w-full sm:w-auto text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none ${
                      isMapped ? 'text-teal-700' : 'text-gray-500'
                    }`}
                  >
                    {MAPPING_FIELDS.map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>

                  {/* 화살표 / 점 — sm 이상에서만 표시 */}
                  <span className={`hidden sm:flex justify-center text-sm ${isMapped ? 'text-teal-400' : 'text-gray-300'}`}>
                    {isMapped ? '→' : '·'}
                  </span>

                  {/* 택배사 엑셀 컬럼명 — sm 이상에서만 이 위치에 표시 */}
                  <span className="hidden sm:block text-sm text-gray-800 font-mono truncate" title={mapping.columnName}>
                    {mapping.columnName}
                  </span>

                  {/* 상태 아이콘 */}
                  <div className="flex justify-center shrink-0">
                    {isMapped ? (
                      <CheckCircle className="h-4 w-4 text-teal-500" />
                    ) : (
                      <Minus className="h-4 w-4 text-gray-300" />
                    )}
                  </div>
                </div>

                {/* 모바일: 2줄 — 화살표 + 컬럼명 */}
                <div className="flex items-center gap-1.5 sm:hidden">
                  <span className={`text-xs ${isMapped ? 'text-teal-400' : 'text-gray-300'}`}>
                    {isMapped ? '↓' : '·'}
                  </span>
                  <span className="text-xs text-gray-500 font-mono truncate" title={mapping.columnName}>
                    {mapping.columnName}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 힌트 */}
      <div className="flex items-start gap-2 text-xs text-gray-500">
        <Lightbulb className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
        <span>"매핑 안 함" 컬럼은 빈 칸으로 내보내집니다.</span>
      </div>

      {/* 버튼 */}
      <div className="flex justify-between pt-2">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          이전
        </Button>
        <Button
          size="sm"
          className="bg-teal-500 hover:bg-teal-600 text-white px-6"
          onClick={onSave}
          disabled={isSaving}
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          {isSaving ? '저장 중...' : '저장하기'}
        </Button>
      </div>
    </div>
  );
}

// ==================== Step 3: 관리 화면 ====================

function StepManage({
  template,
  onEditMapping,
  onDelete,
  onNewTemplate,
}: {
  template: DeliveryTemplate;
  onEditMapping: () => void;
  onDelete: () => void;
  onNewTemplate: () => void;
}) {
  const mappedItems = template.columnMappings.filter((m) => m.mappedField !== 'null');
  const totalColumns = template.columnMappings.length;

  return (
    <div className="space-y-4">
      {/* 양식 카드 */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        {/* 양식 이름 & 정보 */}
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-teal-500" />
          <span className="text-base font-semibold text-gray-800">{template.name}</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {totalColumns}개 컬럼 · {mappedItems.length}개 매핑됨
        </p>

        {/* 매핑 리스트 */}
        <div className="mt-4 space-y-1.5">
          {mappedItems.map((item, index) => {
            const fieldLabel = MAPPING_FIELDS.find((f) => f.value === item.mappedField)?.label ?? item.mappedField;
            return (
              <div key={index} className="text-sm text-gray-600 flex items-center gap-1.5">
                <span className="font-medium text-gray-800">{fieldLabel}</span>
                <span className="text-gray-400">→</span>
                <span className="text-gray-500">{item.columnName}</span>
              </div>
            );
          })}
        </div>

        {/* 액션 버튼 */}
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={onEditMapping}>
            <Settings className="h-3.5 w-3.5 mr-1.5" />
            매핑 수정
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={onDelete}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            양식 삭제
          </Button>
        </div>
      </div>

      {/* 새 양식으로 변경 */}
      <Button
        variant="outline"
        className="w-full"
        onClick={onNewTemplate}
      >
        새 양식으로 변경
      </Button>
    </div>
  );
}
